/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import { uniq } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Activity from 'entities/Activity';
import ActivityAnnouncementAdditionalDetailForm from 'payments/components/ActivityAnnouncementAdditionalDetailForm.jsx';
import ActivityAnnouncementAmountForm from 'payments/components/ActivityAnnouncementAmountForm.jsx';
import ActivityAnnouncementChatMessageFormView from 'payments/components/ActivityAnnouncementChatMessageFormView.jsx';
import ActivityAnnouncementTitleForm from 'payments/components/ActivityAnnouncementTitleForm.jsx';
import ActivityAnnouncementTypePicker, { TYPE_TO_IMG } from 'payments/components/ActivityAnnouncementTypePicker.jsx';
import ActivityActions from 'payments/actions/ActivityActions';
import ActivityRSVPPreview from 'payments/components/ActivityRSVPPreview.jsx';
import ChatActions from 'chat/actions/ChatActions';
import CreateEventCostEducation from 'payments/components/CreateEventCostEducation.jsx';
import CurrentUser from 'auth/CurrentUser';
import Hovercard from 'components/Hovercard.jsx';
import OverlayLoading from 'components/OverlayLoading.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import { mountModal } from 'lib/modal';
import RelatedUserSummary from 'entities/RelatedUserSummary';
import TitleHeader from 'components/TitleHeader.jsx';
import ToastActions from 'actions/ToastActions';
import { EVERYONE } from 'utils/roleHelpers';
import { TARGET_TYPES } from 'entities/Message';
import { reset } from 'reducers/roleBasedMessagingSearch';

import type { ActivityCreateParams } from 'payments/constants/ActivityTypes';
import type { ActivityTypeType } from 'entities/Activity';
import type { Target } from 'entities/Message';

import './styles/ActivityAnnouncementTeacherComposer.scss';

type Page =
  'type' |
  'title' |
  'location_and_date' |
  'amount' |
  'chat_message_and_recipient';

// InitialValues is State without page or groupCodes
// Any fields may be excluded
export type InitialValues = {
  title?: ?string,
  location?: ?string,
  date?: ?Date,
  endDate?: ?Date,
  amount?: ?number,
  type?: ?ActivityTypeType,
  hasChildren?: ?boolean,
  disableReplies?: ?boolean,
  message?: ?string,
};

type Props = {
  defaultGroupCodes: ?string[],
  initialState: InitialValues,
  onActivitySent: Function,
  deselect: () => any,
};

type State = {
  page: Page,
  title: ?string,
  groupCodes: string[],
  location: ?string,
  date: ?Date,
  endDate: ?Date,
  amount: ?number,
  type: ?ActivityTypeType,
  hasChildren: ?boolean,
  disableReplies: ?boolean,
  message: ?string,
  targets: ?Target[],
  inComposer: boolean,
  collectOnline?: boolean,
  loading: boolean,
};

export type PublicState = {
  type: ?ActivityTypeType,
}

const TITLE_PLACEHOLDER_MAPPING = {
  field_trip: 'E.g. Day at the Aquarium',
  event: 'E.g. Spring Choir Concert',
  fundraiser: 'E.g. PTO Car Wash',
  order: 'E.g. Spring Training T-Shirts',
};

class ActivityAnnouncementTeacherComposer extends React.Component {
  props: Props;

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
    params: React.PropTypes.object.isRequired,
  };

  static defaultProps = {
    initialState: {},
  };

  state: State = {
    page: this.props.initialState.type ? 'title' : 'type',
    title: this.props.initialState.title || null,
    groupCodes: this.props.defaultGroupCodes || [],
    location: this.props.initialState.location || null,
    date: this.props.initialState.date || null,
    endDate: this.props.initialState.endDate || null,
    amount: this.props.initialState.amount || null,
    type: this.props.initialState.type || null,
    hasChildren: this.props.initialState.hasChildren || false,
    disableReplies: this.props.initialState.disableReplies || false,
    message: this.props.initialState.message || null,
    targets: [],
    inComposer: false,
    loading: false,
  }

  componentWillUnmount() {
    if (this.props.deselect) {
      this.props.deselect();
    }
  }

  // public
  getPublicState(): PublicState {
    return {
      type: this.state.type,
    };
  }

  mountRSVPPreview = () => {
    const requester = new RelatedUserSummary({
      display_name: CurrentUser.getUser().getDisplayName(),
    });

    const previewActivity = new Activity({
      title: this.state.title,
      amount_per_person: this.state.amount,
      location: this.state.location,
      has_children: this.state.hasChildren,
      start_at: this.state.date,
      end_at: this.state.endDate,
      group_ids: null,
      collect_online: !!this.state.collectOnline,
      recipient_ids: null,
      disable_replies: this.state.disableReplies,
      welcome_msg_body: this.state.message,
      event_type: this.state.type,
      targets: null,
      requester,
    });
    mountModal(<ActivityRSVPPreview isUnderage={false} activity={previewActivity} />, this);
  }

  handlePreviewClick = (values: {message: string, hasChildren: boolean, disableReplies: boolean}) => {
    this.setState({ ...values }, this.mountRSVPPreview);
  }

  createActivityFromCurrentState() {
    const groupIds = [];
    const individualIds = [];
    const roleTargets = []; // group targets with roles other than EVERYONE

    // FIXME: Once the backend handles group roles on activity creation
    // all targets can be passed directly through to the backend.
    if (this.state.targets) {
      this.state.targets.forEach((t: Target) => {
        if (t.type === TARGET_TYPES.GROUP_TARGET) {
          if (t.role === EVERYONE) {
            groupIds.push(t.group_id);
          } else {
            roleTargets.push(t);
          }
        } else {
          individualIds.push(t.member_id);
        }
      });
    }

    const onSuccess = (activity) => {
      const chatUuid = activity.getChatUuid();
      ChatActions.loadChat(chatUuid).then(() => {
        this.context.router.push({
          pathname: `/chat/${chatUuid}`,
        });
      });
    };

    const onError = (error) => {
      ToastActions.showAPIError(error);
      this.setState({ page: 'chat_message_and_recipient' });
    };

    // typecheck: ignore statement
    const params: ActivityCreateParams = {
      title: this.state.title || '',
      amountPerPerson: this.state.amount,
      location: this.state.location,
      date: this.state.date,
      endDate: this.state.endDate,
      hasChildren: this.state.hasChildren,
      groupIds: uniq(groupIds),
      collectOnline: !!this.state.collectOnline,
      disableReplies: this.state.disableReplies,
      welcomeMsgBody: this.state.message,
      type: this.state.type,
      targets: roleTargets,
      individualIds,
    };

    this.setState({ loading: true });

    ActivityActions
      .create(params)
      .then(onSuccess)
      .then(this.props.onActivitySent, onError)
      .then(() => this.setState({ loading: false }));
  }

  getActivityImageType(): any {
    if (this.state.type) {
      return TYPE_TO_IMG[this.state.type];
    } else {
      return undefined;
    }
  }

  getPlaceholder() {
    return TITLE_PLACEHOLDER_MAPPING[this.state.type || 'field_trip'];
  }

  handleInComposer = (inComposer: boolean) => {
    this.setState({ inComposer });
  };

  renderTypeView(onBack: ?Function, onNext: Function) {
    return (
      <div className="ActivityAnnouncementTeacherComposer-view">
        <TitleHeader
          title="What type of activity would you like to organize?"
          bottomMargin="small"
        />
        <ActivityAnnouncementTypePicker
          type={this.state.type}
          onSelect={onNext}
        />
      </div>
    );
  }

  renderTitleView(onBack: Function, onNext: Function) {
    return (
      <div className="ActivityAnnouncementTeacherComposer-view">
        <TitleHeader
          image={this.getActivityImageType()}
          imageClassName="ActivityAnnouncementTeacherComposer-img"
          title="Great! Let's get this show on the road."
          bottomMargin="xlarge"
        />
        <ActivityAnnouncementTitleForm
          title={this.state.title}
          onBack={onBack}
          onNext={onNext}
          placeholder={this.getPlaceholder()}
        />
      </div>
    );
  }

  renderAmountView(onBack: Function, onNext: Function) {
    const subText = (
      <span>
        You can collect money on Remind or through a different method.
        Funds on Remind can be deposited directly into your school’s bank account.
        <LayerTrigger
          trigger="click"
          layer={
            <Hovercard className="ActivityAnnouncementTeacherComposer-amountHovercard">
              <CreateEventCostEducation />
            </Hovercard>
          }>
          <Link
            className="ActivityAnnouncementTeacherComposer-link"
            eventElementName="ActivityAnnouncementTeacherComposer-link">
            {' '}Learn More
          </Link>
        </LayerTrigger>
      </span>
    );

    return (
      <div className="ActivityAnnouncementTeacherComposer-view">
        <TitleHeader
          image={this.getActivityImageType()}
          imageClassName="ActivityAnnouncementTeacherComposer-img"
          title={`How much does ${this.state.title || 'this activity'} cost per person? `}
          subtext={subText}
          bottomMargin="xlarge"
        />
        <ActivityAnnouncementAmountForm
          initialCollectedOnline={this.state.collectOnline}
          initialAmount={this.state.amount}
          onBack={onBack}
          onNext={onNext}
        />
      </div>
    );
  }

  renderLocationAndDateView(onBack: Function, onNext: Function) {
    return (
      <div className="ActivityAnnouncementTeacherComposer-view">
        <TitleHeader
          image={this.getActivityImageType()}
          imageClassName="ActivityAnnouncementTeacherComposer-img"
          title="Now, the basics."
          bottomMargin="xlarge"
        />
        <ActivityAnnouncementAdditionalDetailForm
          withoutAmountInput
          location={this.state.location}
          date={this.state.date}
          endDate={this.state.endDate}
          onBack={onBack}
          onNext={onNext}
        />
      </div>
    );
  }

  renderChatMessageAndRecipientFormView(onBack: Function, onNext: Function) {
    const inComposerExperimentVariation = CurrentUser.inExpVariant(
      'ACTIVITY_CREATION_INDIVIDUAL_COMPOSER',
      'enabled',
    );

    return (
      <div className={cx({ 'ActivityAnnouncementTeacherComposer-view': this.state.inComposer })}>
        <TitleHeader
          image={this.getActivityImageType()}
          imageClassName="ActivityAnnouncementTeacherComposer-img"
          title="Almost there! Add a message to your invitation."
          bottomMargin="xlarge"
        />
        <ActivityAnnouncementChatMessageFormView
          withRecipientInput
          message={this.state.message}
          groupCodes={this.state.groupCodes}
          hasChildren={this.state.hasChildren}
          disableReplies={this.state.disableReplies}
          onBack={onBack}
          onNext={onNext}
          onPreviewClick={this.handlePreviewClick}
          onInComposerChange={this.handleInComposer}
          title={this.state.title || ''}
          showIndividualRecipients={inComposerExperimentVariation}
        />
      </div>
    );
  }

  getPage(): any {
    const canCollectMoney = (
      CurrentUser.isSignedIn() &&
      CurrentUser.getUser().canCollectMoney()
    );

    switch (this.state.page) {
      case 'type':
        return this.renderTypeView(
          undefined,
          (values) => this.setState({ ...values, page: 'title' }),
        );

      case 'title':
        return this.renderTitleView(
          (values) => this.setState({ ...values, page: 'type' }),
          (values) => this.setState({ ...values, page: 'location_and_date' }),
        );

      case 'location_and_date':
        return this.renderLocationAndDateView(
          (values) => this.setState({ ...values, page: 'title' }),
          (values) => this.setState({ ...values, page: canCollectMoney ? 'amount' : 'chat_message_and_recipient' }),
        );

      case 'amount':
        return this.renderAmountView(
          (values) => this.setState({ ...values, page: 'location_and_date' }),
          (values) => this.setState({ ...values, page: 'chat_message_and_recipient' }),
        );

      case 'chat_message_and_recipient':
        return this.renderChatMessageAndRecipientFormView(
          (values) => this.setState({ ...values, page: canCollectMoney ? 'amount' : 'location_and_date' }),
          (values) => this.setState({ ...values }, this.createActivityFromCurrentState),
        );

      default:
        return <span />;
    }
  }

  render() {
    return (
      <div className="ActivityAnnouncementTeacherComposer" data-test-selector="activity-announcement-composer-modal">
        {this.state.loading && <OverlayLoading />}
        <div className="ActivityAnnouncementTeacherComposer-content">
          {this.getPage()}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators({
    deselect: reset,
  }, dispatch);

export { ActivityAnnouncementTeacherComposer };
export default connect(undefined, mapDispatchToProps, undefined, { withRef: true })(ActivityAnnouncementTeacherComposer);



// WEBPACK FOOTER //
// ./src/payments/components/ActivityAnnouncementTeacherComposer.jsx